import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import Button from "../../../../components/controls/Button";

export default function AvailabilityTemplatesSection({
  templates,
  collapsedDays,
  dayNames,
  onToggleDay,
  onAddSlot,
  onRemoveSlot,
  onUpdateSlot,
  loading,
}) {
  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }
  if (!templates || templates.length === 0) {
    return (
      <div className="text-center py-10 text-gray-400">
        Aucune disponibilité configurée.
      </div>
    );
  }

  console.log(
    templates,
    collapsedDays,
    dayNames,
    onToggleDay,
    onAddSlot,
    onRemoveSlot,
    onUpdateSlot,
    loading
  );

  return (
    <div className="space-y-4">
      {templates.map((day) => (
        <div
          key={day.dayOfWeek}
          className="card bg-base-50 border border-black-500 shadow-lg"
        >
          <div className="card-body p-4">
            {/* HEADER JOUR COLLAPSABLE */}
            <div
              className={`flex items-center justify-between cursor-pointer select-none py-2 ${
                !collapsedDays[day.dayOfWeek] ? "border-b mb-1" : ""
              }`}
              onClick={() => onToggleDay(day.dayOfWeek)}
            >
              <h4 className="font-bold text-lg text-primary flex items-center gap-2">
                {dayNames[day.dayOfWeek]}

                {/* Pastille du nombre de créneaux */}
                <span className="bg-ananas text-primary text-xs px-2 py-0.5 rounded-full">
                  {day.slots?.length ?? 0}
                </span>
              </h4>

              <svg
                className={`w-5 h-5 transition-transform duration-200 ${
                  !collapsedDays[day.dayOfWeek] ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {/* SI COLLAPSÉ → RIEN */}
            {!collapsedDays[day.dayOfWeek] && (
              <>
                {/* Bouton ajouter créneau */}
                <div className="flex justify-end mb-3">
                  <Button
                    as="button"
                    type="button"
                    onClick={() => onAddSlot(day.dayOfWeek)}
                    color="primary"
                    size="sm"
                    variant="outline"
                    rightIcon={<PlusIcon className="w-4 h-4" />}
                  >
                    Ajouter un créneau
                  </Button>
                </div>

                {/* Aucun créneau */}
                {!day.slots || day.slots.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 italic">
                    Aucun créneau défini - Service fermé ce jour
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(day.slots ?? []).map((slot, slotIndex) => (
                      <div
                        key={slotIndex}
                        className="p-4 bg-yellow-50 border-l-4 border-yellow-400 shadow-lg"
                      >
                        {/* Header créneau */}
                        <div className="flex items-center justify-between pb-2 border-b border-base-300">
                          <span className="font-semibold text-sm text-gray-700">
                            Créneau {slotIndex + 1}
                          </span>

                          <Button
                            as="delete"
                            type="button"
                            onClick={() =>
                              onRemoveSlot(day.dayOfWeek, slotIndex)
                            }
                            color="mint"
                            size="sm"
                            variant="outline"
                            rightIcon={<TrashIcon className="w-4 h-4" />}
                          >
                            Supprimer
                          </Button>
                        </div>

                        {/* Plage horaire */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div className="form-control">
                            <label className="label">
                              <span className="label-text font-medium">
                                Heure de début
                              </span>
                            </label>
                            <input
                              type="time"
                              className="input input-bordered w-full"
                              value={slot.startTime}
                              onChange={(e) =>
                                onUpdateSlot(
                                  day.dayOfWeek,
                                  slotIndex,
                                  "startTime",
                                  e.target.value
                                )
                              }
                            />
                          </div>

                          <div className="form-control">
                            <label className="label">
                              <span className="label-text font-medium">
                                Heure de fin
                              </span>
                            </label>
                            <input
                              type="time"
                              className="input input-bordered w-full"
                              value={slot.endTime}
                              onChange={(e) =>
                                onUpdateSlot(
                                  day.dayOfWeek,
                                  slotIndex,
                                  "endTime",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>

                        {/* Capacité + prix override */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div className="form-control">
                            <label className="label">
                              <span className="label-text font-medium">
                                Capacité
                              </span>
                              <span className="label-text-alt text-gray-500">
                                Réservations max.
                              </span>
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="100"
                              className="input input-bordered w-full"
                              value={slot.maxBookings}
                              onChange={(e) =>
                                onUpdateSlot(
                                  day.dayOfWeek,
                                  slotIndex,
                                  "maxBookings",
                                  parseInt(e.target.value) || 1
                                )
                              }
                            />
                          </div>

                          <div className="form-control">
                            <label className="label">
                              <span className="label-text font-medium">
                                Prix override
                              </span>
                              <span className="label-text-alt text-gray-500">
                                Optionnel
                              </span>
                            </label>
                            <label className="input-group">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Prix par défaut"
                                className="input input-bordered w-full"
                                value={slot.priceOverride || ""}
                                onChange={(e) =>
                                  onUpdateSlot(
                                    day.dayOfWeek,
                                    slotIndex,
                                    "priceOverride",
                                    e.target.value
                                      ? parseFloat(e.target.value)
                                      : null
                                  )
                                }
                              />
                              <span>$</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
